import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, Edit, Copy, Trash2, Archive, Globe, Download, Upload, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const StatusBadge = ({ status }) => {
  const styles = {
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    archived: 'bg-zinc-100 text-zinc-400 border-zinc-200',
  };
  return <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold ${styles[status] || ''}`}>{status}</Badge>;
};

function SortableRow({ t, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: t.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
  };
  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`template-row-${t.id}`}>
      <TableCell className="px-2 py-2.5 w-8">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500 touch-none" data-testid={`drag-handle-${t.id}`}>
          <GripVertical size={14} />
        </div>
      </TableCell>
      {children}
    </TableRow>
  );
}

export default function TemplatesListPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    const params = filter ? `?status=${filter}` : '';
    api.get(`/templates${params}`).then(r => { setTemplates(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = templates.findIndex(t => t.id === active.id);
    const newIndex = templates.findIndex(t => t.id === over.id);
    const reordered = arrayMove([...templates], oldIndex, newIndex);
    setTemplates(reordered);
    try {
      await api.patch('/templates/reorder', { template_ids: reordered.map(t => t.id) });
      toast.success('Template order saved');
    } catch (err) {
      toast.error('Failed to save order');
      load();
    }
  };

  const handleCreate = async () => {
    try {
      const res = await api.post('/templates', { title: 'Untitled Template', description: '' });
      navigate(`/admin/templates/${res.data.id}/edit`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error creating template'); }
  };

  const handleDuplicate = async (id) => {
    try { await api.post(`/templates/${id}/duplicate`); toast.success('Template duplicated'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/templates/${id}`); toast.success('Template deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleStatus = async (id, status) => {
    try { await api.patch(`/templates/${id}/status`, { status }); toast.success(`Template ${status}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleExport = async (id, title) => {
    try {
      const res = await api.get(`/templates/${id}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(title || 'template').replace(/[^a-zA-Z0-9_-]/g, '_')}.checkbuild.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template exported');
    } catch (err) { toast.error(err.response?.data?.detail || 'Export failed'); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError('');
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.title || !data.questions) {
        setImportError('Invalid file: must contain "title" and "questions" fields');
        setImporting(false);
        return;
      }
      const res = await api.post('/templates/import', data);
      setShowImport(false);
      toast.success('Template imported');
      load();
      navigate(`/admin/templates/${res.data.id}/edit`);
    } catch (err) {
      if (err instanceof SyntaxError) setImportError('Invalid JSON file');
      else setImportError(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filters = ['', 'draft', 'published', 'archived'];

  return (
    <div className="p-6" data-testid="templates-list-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-zinc-500">Build and manage checklist templates. Drag rows to set the order users see them.</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-template-btn" className="rounded-sm text-xs gap-1.5">
            <Upload size={13} /> Import
          </Button>
          <Button size="sm" onClick={handleCreate} data-testid="create-template-btn" className="rounded-sm text-xs gap-1.5"
            style={{ background: 'var(--brand-primary, #1C3557)' }}>
            <Plus size={13} /> New template
          </Button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {filters.map(s => (
          <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm" onClick={() => setFilter(s)}
            className={`rounded-sm text-xs ${filter === s ? 'bg-zinc-900 text-white' : ''}`}>
            {s || 'All'}
          </Button>
        ))}
      </div>

      {loading ? <div className="text-sm text-zinc-400 py-8">Loading...</div> : templates.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm px-4 py-12 text-center text-sm text-zinc-400">No templates found</div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8 px-2" />
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Template</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Questions</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Version</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={templates.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {templates.map(t => (
                    <SortableRow key={t.id} t={t}>
                      <TableCell className="px-4 py-2.5">
                        <div className="font-semibold text-zinc-800">{t.title}</div>
                        {t.description && <div className="text-xs text-zinc-400 truncate max-w-[240px]">{t.description}</div>}
                      </TableCell>
                      <TableCell className="px-4 py-2.5 text-zinc-500">{t.question_count || 0}</TableCell>
                      <TableCell className="px-4 py-2.5 text-zinc-500 font-medium">v{t.version}</TableCell>
                      <TableCell className="px-4 py-2.5"><StatusBadge status={t.status} /></TableCell>
                      <TableCell className="px-4 py-2.5">
                        <div className="flex gap-1">
                          <Tooltip><TooltipTrigger asChild>
                            <Link to={`/admin/templates/${t.id}/edit`} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Edit size={14} /></Link>
                          </TooltipTrigger><TooltipContent><p>Edit</p></TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild>
                            <button onClick={() => handleDuplicate(t.id)} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Copy size={14} /></button>
                          </TooltipTrigger><TooltipContent><p>Duplicate</p></TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild>
                            <button onClick={() => handleExport(t.id, t.title)} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100" data-testid={`export-btn-${t.id}`}><Download size={14} /></button>
                          </TooltipTrigger><TooltipContent><p>Export</p></TooltipContent></Tooltip>
                          {t.status === 'draft' && <Tooltip><TooltipTrigger asChild>
                            <button onClick={() => handleStatus(t.id, 'published')} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50"><Globe size={14} /></button>
                          </TooltipTrigger><TooltipContent><p>Publish</p></TooltipContent></Tooltip>}
                          {t.status === 'published' && <Tooltip><TooltipTrigger asChild>
                            <button onClick={() => handleStatus(t.id, 'archived')} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-amber-600 hover:bg-amber-50"><Archive size={14} /></button>
                          </TooltipTrigger><TooltipContent><p>Archive</p></TooltipContent></Tooltip>}
                          {t.status === 'draft' && <Tooltip><TooltipTrigger asChild>
                            <button onClick={() => handleDelete(t.id)} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
                          </TooltipTrigger><TooltipContent><p>Delete</p></TooltipContent></Tooltip>}
                        </div>
                      </TableCell>
                    </SortableRow>
                  ))}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </div>
      )}

      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="sm:max-w-[420px] rounded-sm" data-testid="import-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Import Template</DialogTitle>
            <p className="text-xs text-zinc-500">Upload a .checkbuild.json file exported from CheckBuild</p>
          </DialogHeader>
          {importError && <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="import-error">{importError}</div>}
          <label className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-zinc-300 rounded-sm cursor-pointer hover:border-zinc-400 transition-colors">
            <Upload size={22} className="text-zinc-300 mb-2" />
            <span className="text-xs text-zinc-400 font-medium">{importing ? 'Importing...' : 'Click to select JSON file'}</span>
            <input ref={fileInputRef} type="file" accept=".json" onChange={handleImportFile} className="hidden" data-testid="import-file-input" disabled={importing} />
          </label>
        </DialogContent>
      </Dialog>
    </div>
  );
}
